import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

import differenceInMinutes from 'date-fns/difference_in_minutes';
import startOfDay from 'date-fns/start_of_day';
import format from 'date-fns/format';

import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import flatMap from 'lodash/flatMap';

import { observer } from 'mobx-react-lite';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { Job, Task } from 'models';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';

interface Props {
    jobs: Job[];
    startDate: Date;
}

const CustomTooltip = (props: any) => (
    <DefaultTooltipContent
        {...props}
        payload={props.payload.slice().reverse()}
        labelFormatter={(label: number) => format(new Date(label), 'dddd Do MMMM YYYY')}
    />
);

export default observer<Props>(function TaskLatenessChart({ jobs, startDate }) {
    const groupByLateTiers = (tasks: Task[], dates: Date[]) => {
        const tiers = dates.map((d) => ({
            completionDate: new Date(d).getTime(),
            lateTiers: {
                veryLate: 0,
                late: 0,
                onTime: 0,
                early: 0
            }
        }));
        tasks.forEach((t) => {
            const minutes = differenceInMinutes(
                new Date(t.completed!.time!),
                new Date(t.timeframe!.by!)
            );

            const ix = tiers.findIndex(
                (d) => d.completionDate === startOfDay(t.completed!.time!).getTime()
            );
            if (ix > -1) {
                if (minutes >= 15) {
                    tiers[ix]!.lateTiers.veryLate++;
                } else if (5 <= minutes && minutes < 15) {
                    tiers[ix]!.lateTiers.late++;
                } else if (-15 < minutes && minutes < 5) {
                    tiers[ix]!.lateTiers.onTime++;
                } else if (minutes <= -15) {
                    tiers[ix]!.lateTiers.early++;
                }
            }
        });

        return tiers;
    };

    const getTaskDates = (tasks: Task[]) => {
        const dates: Date[] = [];

        tasks.forEach((t) => {
            if (t.completed && t.timeframe!.by) {
                dates.push(startOfDay(new Date(t.completed.time!)));
            }
        });

        const result = sortBy(
            uniqBy(dates, (d) => d.getTime()),
            (d) => d.getTime()
        );

        return result;
    };

    const calculatePercentage = (tasksInTier: number, props: any) => {
        const totalTasks = Object.values(props.payload.lateTiers as number[]).reduce(
            (prev: number, curr: number) => prev + curr,
            0
        );
        const formattedPercentage = `${(
            (tasksInTier / (totalTasks > 0 ? totalTasks : 1)) *
            100
        ).toFixed(1)}%`;
        return formattedPercentage;
    };

    const tasks = flatMap(jobs, (it) =>
        it.tasks.items.filter(
            (it) => it.completed && it.timeframe!.by && it.completed!.time! > startDate.valueOf()
        )
    );

    const lateData = groupByLateTiers(tasks, getTaskDates(tasks));

    if (lateData.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    height: '80%',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                No task deadlines on completed jobs.
            </div>
        );
    }

    return (
        <ObservableAutoSizer customChildren={({ width }: { width: number }) => (
            <AreaChart
                width={width}
                height={200}
                data={lateData}
                stackOffset="expand"
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <XAxis
                    dataKey="completionDate"
                    minTickGap={100}
                    tickFormatter={(t) => format(new Date(t), 'Do MMM')}
                />
                <YAxis tickFormatter={(t) => `${t * 100}%`} />
                <Tooltip
                    content={<CustomTooltip />}
                    labelFormatter={(label) => format(new Date(label), 'dddd Do MMMM YYYY')}
                    formatter={(value: number, name: string, props: any) => [
                        calculatePercentage(value, props),
                        name
                    ]}
                />
                <Area
                    dataKey="lateTiers.early"
                    name="15+ mins Early"
                    stackId="1"
                    stroke="#22ada6"
                    fill="#78e2dd"
                />
                <Area
                    dataKey="lateTiers.onTime"
                    name="On Time"
                    stackId="1"
                    stroke="#38b75e"
                    fill="#82ffa7"
                />
                <Area
                    dataKey="lateTiers.late"
                    name="5 to 15 mins Late"
                    stackId="1"
                    stroke="#ffc23f"
                    fill="#ffd375"
                />
                <Area
                    dataKey="lateTiers.veryLate"
                    name="15+ mins Late"
                    stackId="1"
                    stroke="#ff3f2b"
                    fill="#ff604f"
                />
            </AreaChart>
        )} />
    );
});
